<div class="chat-container">
  <!-- Messages Container -->
  <div class="messages-container" #messageContainer (scroll)="onScroll($event)">
    <!-- Loading More Indicator -->
    <div *ngIf="isLoadingMore" class="loading-more">
      <div class="spinner"></div>
      <span>{{ "chat.loadingMore" | translate }}</span>
    </div>

    <!-- Initial Loading -->
    <div *ngIf="isLoading" class="loading-initial">
      <div class="spinner-large"></div>
      <p>{{ "chat.loading" | translate }}</p>
    </div>

    <!-- Messages List -->
    <div *ngIf="!isLoading && messages?.length > 0" class="messages-list">
      <!-- Message Items -->
      <div
        *ngFor="let message of messages; trackBy: trackMessageById"
        class="message-wrapper"
        [ngClass]="{ 'own-message': isOwnMessage(message) }"
      >
        <!-- Avatar -->
        <div class="message-avatar">
          <img
            *ngIf="getUserAvatar(message.senderId)"
            [src]="getUserAvatar(message.senderId)"
            [alt]="message.senderName"
            class="avatar-image"
          />
          <span *ngIf="!getUserAvatar(message.senderId)">
            {{ getInitials(message.senderName) }}
          </span>
        </div>

        <!-- Message Content Area -->
        <div class="message-content">
          <!-- Header: Name + Time -->
          <div class="message-header">
            <span class="sender-name">{{ message.senderName }}</span>
            <span class="message-time">{{
              formatTime(message.createdDate)
            }}</span>
          </div>

          <ng-container
            *ngIf="isConsultationActionMessage(message); else standardMessage"
          >
            <div class="consultation-action-card">
              <div class="consultation-card-title">
                <i class="fa-solid fa-stethoscope"></i>
                <span>{{ "chat.consultation" | translate }}</span>
              </div>

              <div *ngIf="message.message" class="consultation-card-message">
                {{ message.message }}
              </div>

              <div
                *ngIf="
                  message.chatMessageImages &&
                  message.chatMessageImages.length > 0
                "
                class="consultation-media-strip"
              >
                <ng-container
                  *ngFor="
                    let attachment of message.chatMessageImages;
                    let imageIndex = index
                  "
                >
                  <button
                    *ngIf="isImageFile(attachment.imagePath)"
                    type="button"
                    class="consultation-media-item"
                    (click)="openAttachmentPreview(attachment.imagePath)"
                    [title]="getFileName(attachment.imagePath)"
                  >
                    <img
                      [src]="attachment.imagePath"
                      [alt]="getFileName(attachment.imagePath)"
                      class="consultation-media-image"
                      (load)="scrollToBottom()"
                    />
                    <span class="consultation-media-index">
                      {{ imageIndex + 1 }}/{{
                        message.chatMessageImages?.length || 0
                      }}
                    </span>
                  </button>

                  <button
                    *ngIf="!isImageFile(attachment.imagePath)"
                    type="button"
                    class="consultation-file-item"
                    (click)="openAttachmentPreview(attachment.imagePath)"
                    [title]="getFileName(attachment.imagePath)"
                  >
                    <div class="file-info">
                      <i
                        class="bi"
                        [ngClass]="getFileIcon(attachment.imagePath)"
                      ></i>
                      <span class="file-name">{{
                        getFileName(attachment.imagePath)
                      }}</span>
                    </div>
                    <div class="file-action">
                      <i class="bi bi-eye"></i>
                    </div>
                  </button>
                </ng-container>
              </div>

              <div
                *ngIf="message.canAnswerConsultation"
                class="consultation-question"
              >
                <span>{{ "woundConsultationDetail.hasAWound" | translate }}</span>
                <div class="consultation-actions">
                  <button
                    type="button"
                    class="consultation-btn"
                    (click)="answerConsultation(message, true)"
                    [disabled]="isAnsweringConsultation(message)"
                  >
                    {{ "woundConsultationDetail.yes" | translate }}
                  </button>
                  <button
                    type="button"
                    class="consultation-btn secondary"
                    (click)="answerConsultation(message, false)"
                    [disabled]="isAnsweringConsultation(message)"
                  >
                    {{ "woundConsultationDetail.no" | translate }}
                  </button>
                </div>
              </div>

              <div
                *ngIf="showSpecialistConsultationCompleted(message)"
                class="consultation-muted consultation-complete-note"
              >
                <span>{{ "chat.consultationCompletedForDoctor" | translate }}</span>
              </div>

              <div
                *ngIf="isNurse && !message.canViewConsultationAnswer"
                class="consultation-muted"
              >
                {{ "chat.consultationWaiting" | translate }}
              </div>

              <div
                *ngIf="message.canViewConsultationAnswer"
                class="consultation-result"
              >
                <div class="consultation-decision">
                  <span>{{ "chat.consultationDecision" | translate }}</span>
                  <strong>{{
                    getConsultationAnswerLabel(message) | translate
                  }}</strong>
                </div>

                <button
                  *ngIf="message.canOpenConsultationAction"
                  type="button"
                  class="consultation-primary-action"
                  (click)="openConsultationAction(message)"
                >
                  {{ getConsultationActionLabel(message) | translate }}
                </button>

                <div
                  *ngIf="message.consultationActionCompleted"
                  class="consultation-muted"
                >
                  {{ "chat.consultationCompleted" | translate }}
                </div>
              </div>
            </div>
          </ng-container>

          <ng-template #standardMessage>
            <div *ngIf="message.message" class="message-bubble">
              <div class="message-text">{{ message.message }}</div>
            </div>

            <!-- Attachments (Outside Bubble) -->
            <div
              *ngIf="
                message.chatMessageImages &&
                message.chatMessageImages?.length > 0
              "
              class="message-attachments"
            >
              <ng-container
                *ngFor="let attachment of message.chatMessageImages"
              >
                <!-- Image Attachment -->
                <img
                  *ngIf="isImageFile(attachment.imagePath)"
                  [src]="attachment.imagePath"
                  [alt]="'Attachment'"
                  class="message-image"
                  (click)="openAttachmentPreview(attachment.imagePath)"
                  (load)="scrollToBottom()"
                />

                <!-- File Attachment (Non-image) -->
                <div
                  *ngIf="!isImageFile(attachment.imagePath)"
                  class="file-attachment-card"
                  (click)="openAttachmentPreview(attachment.imagePath)"
                  [title]="getFileName(attachment.imagePath)"
                >
                  <div class="file-info">
                    <i
                      class="bi"
                      [ngClass]="getFileIcon(attachment.imagePath)"
                    ></i>
                    <span class="file-name">{{
                      getFileName(attachment.imagePath)
                    }}</span>
                  </div>
                  <div class="file-action">
                    <i class="bi bi-eye"></i>
                  </div>
                </div>
              </ng-container>
            </div>
          </ng-template>
        </div>
      </div>
    </div>

    <!-- Empty State (Outside list for centering) -->
    <div *ngIf="!isLoading && messages?.length === 0" class="empty-state">
      <i class="bi bi-chat-dots"></i>
      <p>{{ "chat.noMessages" | translate }}</p>
      <span>{{ "chat.startConversation" | translate }}</span>
    </div>
  </div>

  <!-- Input Area -->
  <div class="input-container">
    <!-- File Preview -->
    <div *ngIf="selectedFile" class="file-preview">
      <i class="bi bi-file-earmark"></i>
      <span class="file-name">{{ selectedFileName }}</span>
      <button
        type="button"
        class="btn-remove-file"
        (click)="clearFileSelection()"
        [disabled]="isSending"
      >
        <i class="bi bi-x"></i>
      </button>
    </div>

    <!-- Input Row -->
    <div class="input-row">
      <!-- File Input (Hidden) -->
      <input
        type="file"
        #fileInput
        (change)="onFileSelected($event)"
        accept="image/*,.pdf,.doc,.docx"
        style="display: none"
      />

      <!-- Actions Menu Container -->
      <div class="actions-menu-container">
        <!-- Main Toggle Button (+) -->
        <button
          type="button"
          class="btn-actions-toggle"
          (click)="toggleActionsMenu($event)"
          [disabled]="connectionState !== ConnectionState.Connected"
          [class.active]="isActionsMenuOpen"
          title="Actions"
        >
          <i class="bi bi-plus-lg"></i>
        </button>

        <!-- Popup Menu -->
        <div class="actions-popup" *ngIf="isActionsMenuOpen">
          <!-- Audio Call -->
          <button
            type="button"
            class="action-item"
            (click)="audioCall(); isActionsMenuOpen = false"
            [disabled]="isInCall"
            title="Start audio call"
          >
            <i class="bi bi-telephone-fill"></i>
            <span>Audio Call</span>
          </button>

          <!-- Video Call -->
          <button
            type="button"
            class="action-item"
            (click)="videoCall(); isActionsMenuOpen = false"
            [disabled]="isInCall"
            title="Start video call"
          >
            <i class="bi bi-camera-video-fill"></i>
            <span>Video Call</span>
          </button>

          <!-- Attach Button -->
          <button
            type="button"
            class="action-item"
            (click)="triggerFileInput(); isActionsMenuOpen = false"
            title="Attach file"
          >
            <i class="bi bi-paperclip"></i>
            <span>Attach File</span>
          </button>

          <!-- Consultation Button -->
          <button
            *ngIf="isNurse"
            type="button"
            class="action-item"
            (click)="openConsultationModal(); isActionsMenuOpen = false"
            title="Start Consultation"
          >
            <i class="fa-solid fa-stethoscope"></i>
            <span>{{ "chat.consultation" | translate }}</span>
          </button>
        </div>
      </div>

      <!-- Message Input -->
      <textarea
        #messageInput
        class="message-input"
        [(ngModel)]="messageText"
        (keypress)="onKeyPress($event)"
        (input)="autoResizeTextarea()"
        placeholder="Type a message..."
        rows="1"
        [disabled]="isSending || connectionState !== ConnectionState.Connected"
        maxlength="1000"
      ></textarea>

      <!-- Send Button -->
      <button
        type="button"
        class="btn-send"
        (click)="sendMessage()"
        [disabled]="
          (!messageText.trim() && !selectedFile) ||
          isSending ||
          connectionState !== ConnectionState.Connected
        "
      >
        <i *ngIf="!isSending" class="bi bi-send-fill"></i>
        <div *ngIf="isSending" class="spinner-small"></div>
      </button>
    </div>
  </div>

  <!-- Document Preview Modal (Enhanced Lightbox) -->
  <div *ngIf="isLightboxOpen" class="preview-modal" (click)="closePreview()">
    <div class="preview-container" (click)="$event.stopPropagation()">
      <!-- Header -->
      <div class="preview-header">
        <div class="document-info">
          <i
            class="bi"
            [ngClass]="
              previewFileType === 'image' ? 'bi-image' : 'bi-file-earmark-pdf'
            "
          ></i>
          <span class="document-name">{{ previewFileName }}</span>
        </div>

        <div class="header-actions">
          <button
            type="button"
            class="action-btn download-btn"
            (click)="downloadFile(previewFilePath, previewFileName)"
            title="Download"
          >
            <i class="bi bi-download"></i>
            <span>Download</span>
          </button>
          <button
            type="button"
            class="action-btn close-btn"
            (click)="closePreview()"
            title="Close"
          >
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="preview-content">
        <!-- Loading State -->
        <div *ngIf="isPreviewLoading" class="loading-state">
          <div class="spinner-large"></div>
          <p>Loading preview...</p>
        </div>

        <!-- Error State -->
        <div *ngIf="previewHasError && !isPreviewLoading" class="error-state">
          <i class="bi bi-exclamation-triangle"></i>
          <p>Preview not available for this file type.</p>
        </div>

        <!-- PDF Preview -->
        <iframe
          *ngIf="
            !isPreviewLoading &&
            !previewHasError &&
            previewFileType === 'pdf' &&
            previewUrl
          "
          [src]="previewUrl"
          class="pdf-viewer"
          frameborder="0"
        >
        </iframe>

        <!-- Image Preview -->
        <img
          *ngIf="
            !isPreviewLoading &&
            !previewHasError &&
            previewFileType === 'image' &&
            previewUrl
          "
          [src]="previewUrl"
          [alt]="previewFileName"
          class="image-viewer"
        />
      </div>
    </div>
  </div>

  <!-- Contact Selector Modal -->
  <div
    *ngIf="showContactSelector"
    class="contact-selector-overlay"
    (click)="closeContactSelector()"
  >
    <div class="contact-selector-modal" (click)="$event.stopPropagation()">
      <div class="contact-selector-header">
        <h3>{{ "chat.selectContact" | translate }}</h3>
        <button class="btn-close-selector" (click)="closeContactSelector()">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <div class="contact-selector-body">
        <!-- Search Input -->
        <div
          *ngIf="!isLoadingContacts && callContacts.length > 0"
          class="contact-search"
        >
          <i class="bi bi-search"></i>
          <input
            type="text"
            class="search-input"
            [(ngModel)]="contactSearchTerm"
            placeholder="{{ 'chat.searchContacts' | translate }}"
            autocomplete="off"
          />
          <button
            *ngIf="contactSearchTerm"
            class="btn-clear-search"
            (click)="contactSearchTerm = ''"
            type="button"
          >
            <i class="bi bi-x"></i>
          </button>
        </div>

        <!-- Loading State -->
        <div *ngIf="isLoadingContacts" class="loading-contacts">
          <div class="spinner-large"></div>
          <p>{{ "chat.loadingContacts" | translate }}</p>
        </div>

        <!-- Contacts List -->
        <div
          *ngIf="!isLoadingContacts && filteredCallContacts.length > 0"
          class="contacts-list"
        >
          <div
            *ngFor="let contact of filteredCallContacts"
            class="contact-item"
            (click)="initiateCall(contact.id)"
          >
            <div class="contact-avatar">
              <i class="bi bi-person-circle"></i>
            </div>
            <div class="contact-info">
              <span class="contact-name">{{ contact.fullName }}</span>
            </div>
            <div class="contact-action">
              <i
                class="bi"
                [ngClass]="{
                  'bi-telephone-fill': selectedCallType === 'audio',
                  'bi-camera-video-fill': selectedCallType === 'video',
                }"
              ></i>
            </div>
          </div>
        </div>

        <!-- No Results State -->
        <div
          *ngIf="
            !isLoadingContacts &&
            callContacts.length > 0 &&
            filteredCallContacts.length === 0
          "
          class="empty-contacts"
        >
          <i class="bi bi-search"></i>
          <p>{{ "chat.noContactsFound" | translate }}</p>
        </div>

        <!-- Empty State -->
        <div
          *ngIf="!isLoadingContacts && callContacts.length === 0"
          class="empty-contacts"
        >
          <i class="bi bi-person-x"></i>
          <p>{{ "chat.noContactsAvailable" | translate }}</p>
        </div>
      </div>
    </div>
  </div>
</div>
